/**
 * Bootstrap: aguarda mensagem inicial do C# com sessionToken e hmacSecret
 * 
 * O C# pode enviar uma mensagem de inicialização ao carregar o WebView2
 * contendo credenciais e configurações necessárias para comunicação.
 * 
 * Formatos suportados:
 * - `type: 'bootstrap-session'`
 * - `type: 'session:bootstrap'`
 * - `action: 'session:hello'`
 * 
 * ✅ MELHORADO (2025): Adicionado retry automático e diagnóstico detalhado
 * para resolver problemas intermitentes em produção HTTPS.
 */

import { sendToTerminal } from '../terminalLogger';
import { LogCategory } from '../../config/logging';

export interface BootstrapData {
  sessionToken: string;
  hmacSecret?: string;
  features?: any;
}

function isBootstrapMessage(parsedData: any): boolean {
  return (
    parsedData?.type === 'bootstrap-session' ||
    parsedData?.type === 'session:bootstrap' ||
    parsedData?.type === 'tools:session-bootstrap' ||
    parsedData?.type === 'tools:session-token' ||
    parsedData?.action === 'bootstrap-session' ||
    parsedData?.action === 'session:hello' ||
    parsedData?.action === 'tools:bootstrap-session'
  );
}

function extractBootstrapPayload(parsedData: any): any {
  if (parsedData?.data && typeof parsedData.data === 'object') {
    return parsedData.data;
  }

  return parsedData;
}

function buildBootstrapData(payload: any): BootstrapData {
  return {
    sessionToken:
      payload?.sessionToken ||
      payload?.token ||
      (window as any)?.sessionToken ||
      '',
    hmacSecret:
      payload?.hmacSecret ||
      payload?.hmacKeyB64 ||
      payload?.secret ||
      (window as any)?.sessionCrypto?.hmacKeyB64,
    features: payload?.features
  };
}

/**
 * Verifica se o ambiente está pronto para comunicação WebView2.
 * Útil para diagnóstico de problemas intermitentes.
 */
export function diagnoseWebViewEnvironment(): {
  hasWebView: boolean;
  hasSessionToken: boolean;
  hasHmacKey: boolean;
  hasDockerId: boolean;
  tokenValid: boolean;
  protocol: string;
  hostname: string;
  isHttps: boolean;
  timestamp: number;
} {
  const sessionToken = (window as any)?.sessionToken;
  const hmacKey = (window as any)?.sessionCrypto?.hmacKeyB64;
  const dockerId = (window as any)?.dockerId;
  
  const tokenValid = typeof sessionToken === 'string' && 
                     sessionToken.length === 64 && 
                     /^[a-fA-F0-9]+$/.test(sessionToken);
  
  return {
    hasWebView: !!(window as any)?.chrome?.webview,
    hasSessionToken: !!sessionToken,
    hasHmacKey: !!hmacKey,
    hasDockerId: !!dockerId,
    tokenValid,
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    isHttps: window.location.protocol === 'https:',
    timestamp: Date.now()
  };
}

/**
 * Aguarda mensagem de bootstrap do C#.
 * 
 * Esta função escuta mensagens do WebView2 procurando por mensagens de bootstrap
 * que contenham o sessionToken e opcionalmente o hmacSecret.
 * 
 * **Uso:**
 * ```typescript
 * try {
 *   const bootstrap = await waitForBootstrap(5000);
 *   logger.debug('Token recebido:', bootstrap.sessionToken);
 * } catch (error) {
 *   logger.error('Timeout aguardando bootstrap');
 * }
 * ```
 * 
 * @param timeout Tempo máximo de espera em milissegundos (padrão: 10000ms = 10s)
 * @returns Promise que resolve com dados de bootstrap ou rejeita em timeout
 */
export function waitForBootstrap(timeout = 10000): Promise<BootstrapData> {
  return new Promise((resolve, reject) => {
    const hasWebView = !!(window as any).chrome?.webview;
    let messageCount = 0; // ✅ Contador de mensagens recebidas
    
    // Verificar ambiente antes de iniciar
    const diagnosis = diagnoseWebViewEnvironment();

    // Se já temos token válido, resolver imediatamente
    if (diagnosis.tokenValid && diagnosis.hasHmacKey) {
      sendToTerminal('✅ [BOOTSTRAP] Token já disponível', {}, LogCategory.CONNECTION);
      
      resolve({
        sessionToken: (window as any).sessionToken,
        hmacSecret: (window as any).sessionCrypto?.hmacKeyB64,
        features: (window as any)?.__corelFeatures
      });
      return;
    }
    
    const timeoutId = setTimeout(() => {
      cleanup();
      
      // ✅ MELHORADO: Log mais detalhado no timeout com diagnóstico
      const finalDiagnosis = diagnoseWebViewEnvironment();
      
      sendToTerminal('⏱️ [BOOTSTRAP] Timeout aguardando bootstrap', {
        timeout,
        hasWebView,
        messagesReceived: messageCount,
        note: messageCount === 0 
          ? 'Nenhuma mensagem recebida do WebView2 - verifique se C# está enviando bootstrap'
          : `${messageCount} mensagens recebidas mas nenhuma era de bootstrap`,
        // ✅ NOVO: Diagnóstico final
        finalDiagnosis: {
          isHttps: finalDiagnosis.isHttps,
          hostname: finalDiagnosis.hostname,
          hasTokenNow: finalDiagnosis.hasSessionToken,
          hasHmacNow: finalDiagnosis.hasHmacKey,
          tokenValidNow: finalDiagnosis.tokenValid
        }
      }, LogCategory.ERROR);
      
      reject(new Error('Timeout aguardando bootstrap do C#'));
    }, timeout);
    
    const handler = (event: MessageEvent) => {
      messageCount++; // ✅ Incrementar contador
      try {
        const data = event.data;
        
        // Tentar parsear se for string
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        
        // Verificar se é mensagem de bootstrap
        // Backend pode enviar via 'type' ou 'action'
        const isBootstrap = isBootstrapMessage(parsedData);
        
        if (isBootstrap) {
          cleanup();

          const payload = extractBootstrapPayload(parsedData);
          
          const bootstrapData: BootstrapData = buildBootstrapData(payload);
          if (parsedData?.type === 'tools:session-bootstrap') {
            try {
              (window as any).__ONEPRINT_LAST_TOOLS_BOOTSTRAP__ = {
                dockerId: payload?.dockerId,
                sessionEpoch: payload?.sessionEpoch,
                tokenVersion: payload?.tokenVersion,
                sessionToken: payload?.sessionToken,
                hmacKeyB64: payload?.hmacKeyB64,
                frontendUrl: payload?.frontendUrl,
                issuedAt: payload?.issuedAt,
                source: 'bootstrap-listener',
                capturedAt: Date.now()
              };
            } catch {}
          }
          
          sendToTerminal('✅ [BOOTSTRAP] Sessão recebida', {
            hasToken: !!bootstrapData.sessionToken,
            hasHmac: !!bootstrapData.hmacSecret,
            type: parsedData?.type,
            action: parsedData?.action,
            dockerId: payload?.dockerId,
            sessionEpoch: payload?.sessionEpoch,
            tokenVersion: payload?.tokenVersion
          }, LogCategory.CONNECTION);
          
          if (!bootstrapData.sessionToken) {
            sendToTerminal('❌ [BOOTSTRAP] Bootstrap sem sessionToken', {
              parsedKeys: Object.keys(parsedData || {}),
              payloadKeys: Object.keys(payload || {})
            }, LogCategory.ERROR);
            reject(new Error('Bootstrap recebido sem sessionToken'));
            return;
          }
          
          resolve(bootstrapData);
        }
      } catch (error) {
        // Log de erro de parsing
        sendToTerminal('❌ [BOOTSTRAP] Erro ao processar mensagem', {
          error: error instanceof Error ? error.message : String(error)
        }, LogCategory.ERROR);
      }
    };
    
    const cleanup = () => {
      clearTimeout(timeoutId);
      if ((window as any).chrome?.webview) {
        (window as any).chrome.webview.removeEventListener('message', handler);
      }
    };
    
    // Verificar se WebView2 está disponível
    if (hasWebView) {
      (window as any).chrome.webview.addEventListener('message', handler);
    } else {
      cleanup();
      sendToTerminal('❌ [BOOTSTRAP] WebView2 não disponível', {}, LogCategory.ERROR);
      reject(new Error('WebView2 não disponível'));
    }
  });
}

/**
 * Tenta capturar bootstrap com fallback para window.sessionToken.
 * 
 * Esta é uma função helper que primeiro tenta aguardar bootstrap ativo do C#,
 * e em caso de timeout, faz fallback para verificar se sessionToken já está
 * disponível em `window.sessionToken` (injetado via C# diretamente no DOM).
 * 
 * ✅ MELHORADO (2025): Adicionado retry automático com backoff exponencial
 * para resolver problemas intermitentes em produção HTTPS.
 * 
 * **Uso:**
 * ```typescript
 * const bootstrap = await bootstrapWithFallback(3000);
 * // Sempre retorna algo (ou lança erro se nenhum método funcionar)
 * ```
 * 
 * @param timeout Tempo de espera para bootstrap ativo
 * @param maxRetries Número máximo de tentativas (padrão: 3)
 * @returns Promise com dados de bootstrap ou fallback
 */
export async function bootstrapWithFallback(timeout = 10000, maxRetries = 3): Promise<BootstrapData> {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // ✅ NOVO: Log de tentativa
      sendToTerminal(`🔄 [BOOTSTRAP] Tentativa ${attempt}/${maxRetries}`, {
        timeout,
        attempt,
        maxRetries
      }, LogCategory.CONNECTION);
      
      // Tentar aguardar bootstrap ativo
      return await waitForBootstrap(timeout);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Fallback: verificar window.sessionToken (reinjetado pelo C# após refresh)
      const fallbackToken = (window as any)?.sessionToken;
      const fallbackHmac = (window as any)?.sessionCrypto?.hmacKeyB64 || (window as any)?.hmacSecret;
      const hasWebView = !!(window as any)?.chrome?.webview;
      
      sendToTerminal('🔄 [BOOTSTRAP] Fallback ativado', {
        error: lastError.message,
        attempt,
        maxRetries,
        hasWebView,
        hasFallbackToken: !!fallbackToken,
        fallbackTokenLength: fallbackToken?.length ?? 0,
        hasFallbackHmac: !!fallbackHmac,
        dockerId: (window as any)?.dockerId
      }, LogCategory.CONNECTION);
      
      if (fallbackToken) {
        // Validar token antes de usar
        // ✅ FORMATOS ACEITOS: GUID (36 chars) ou Hex (64 chars)
        // O C# usa Guid.NewGuid().ToString() que gera GUID de 36 caracteres
        const tokenStrValidation = typeof fallbackToken === 'string' ? fallbackToken : '';
        const guidRegexValidation = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
        const isGuidValidation = guidRegexValidation.test(tokenStrValidation);
        const isHex64Validation = tokenStrValidation.length === 64 && /^[a-fA-F0-9]+$/.test(tokenStrValidation);
        const tokenValid = isGuidValidation || isHex64Validation;
        
        if (tokenValid) {
          // Fallback bem-sucedido - token encontrado em window
          // ✅ Usar sessionCrypto.hmacKeyB64 conforme padrão do C# após F5 fix
          sendToTerminal('✅ [BOOTSTRAP] Fallback bem-sucedido - token válido em window', {
            tokenLength: fallbackToken.length,
            tokenFormat: isGuidValidation ? 'GUID (36 chars)' : 'Hex (64 chars)',
            tokenPreview: fallbackToken.substring(0, 8) + '...',
            hasHmacSecret: !!fallbackHmac,
            attempt
          }, LogCategory.CONNECTION);
          
          return {
            sessionToken: fallbackToken,
            hmacSecret: fallbackHmac,
            features: (window as any)?.__corelFeatures || (window as any)?.features || undefined
          };
        } else {
          const tokenStrFallback = typeof fallbackToken === 'string' ? fallbackToken : '';
          const guidRegexFallback = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
          const isGuidFallback = guidRegexFallback.test(tokenStrFallback);
          const isHex64Fallback = tokenStrFallback.length === 64 && /^[a-fA-F0-9]+$/.test(tokenStrFallback);
          
          sendToTerminal('⚠️ [BOOTSTRAP] Token em window é inválido', {
            tokenLength: tokenStrFallback.length,
            tokenFormat: isGuidFallback ? 'GUID' : (isHex64Fallback ? 'Hex' : 'desconhecido'),
            expectedFormats: 'GUID (36 chars) ou Hex (64 chars)',
            attempt
          }, LogCategory.CONNECTION);
        }
      }
      
      // Se não é a última tentativa, aguardar antes de tentar novamente
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Backoff exponencial, máx 5s
        sendToTerminal(`⏳ [BOOTSTRAP] Aguardando ${delay}ms antes da próxima tentativa`, {
          attempt,
          nextAttempt: attempt + 1,
          delay
        }, LogCategory.CONNECTION);
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // Todas as tentativas falharam
  const diagnosis = diagnoseWebViewEnvironment();
  const errorMsg = `Bootstrap falhou após ${maxRetries} tentativas. WebView2: ${diagnosis.hasWebView ? 'disponível' : 'indisponível'}, Token: ${diagnosis.hasSessionToken ? 'encontrado' : 'ausente'}, HMAC: ${diagnosis.hasHmacKey ? 'encontrado' : 'ausente'}`;
  
  sendToTerminal('❌ [BOOTSTRAP] Todas as tentativas falharam', {
    error: errorMsg,
    diagnosis,
    lastError: lastError?.message
  }, LogCategory.ERROR);
  
  throw new Error(errorMsg);
}

/**
 * Verifica se a sessão está pronta para comunicação.
 * 
 * ✅ Usar antes de enviar mensagens para garantir que variáveis críticas existem.
 * 
 * @returns boolean indicando se todas as variáveis de sessão estão disponíveis
 */
export function isSessionReady(): boolean {
  return !!(
    (window as any)?.sessionToken && 
    (window as any)?.dockerId && 
    (window as any)?.sessionCrypto?.hmacKeyB64
  );
}

